import React, { useState, useRef, useEffect } from 'react';

import { useApolloClient, useMutation } from '@apollo/react-hooks';
import { Box, Grid } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';

import ImagePassword from 'assets/Alter-Password.svg';
import EyesClosedIcon from 'assets/eyes-closed.svg';
import EyesOpenIcon from 'assets/eyes-open.svg';
import { TypographyCN } from 'components/atoms/Typograph';
import { alterarSenhaMutation } from 'components/ChangePassword/query';
import { CustomButton } from 'components/CustomButton';
import { InputCN } from 'components/InputCN';
import { AlterarSenhaFuncionarioMutation, MutationAlterarSenhaFuncionarioArgs } from 'types/graphql';
import { useMobile } from 'utils/useMobile';

import styled, { keyframes } from 'styled-components';

type DrawerPasswordProps = {
  open: boolean;
  handleCloseDrawer: () => void;
};

const slideIn = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
`;

const StyledChild = styled(Grid)`
  animation: ${slideIn} 0.5s ease-in-out;

  @media screen and (max-width: 689px) {
    animation: none;
  }
`;

type ChangePasswordFormType = {
  senhaAtual: string;
  senhaNova: string;
};

export function DrawerPassword({ open, handleCloseDrawer }: DrawerPasswordProps) {
  const [security, setSecurity] = useState<boolean>(true);
  const [confirmSecurity, setConfirmSecurity] = useState<boolean>(true);
  const { control, getValues, setValue, reset } = useForm<ChangePasswordFormType>();
  const { enqueueSnackbar } = useSnackbar();
  const [alterarSenha, { error }] = useMutation<
    { alterarSenhaFuncionario: AlterarSenhaFuncionarioMutation },
    MutationAlterarSenhaFuncionarioArgs
  >(alterarSenhaMutation);
  const client = useApolloClient();
  const [padding, setPadding] = useState<boolean>(false);

  const childRef = useRef<HTMLDivElement>(null);
  const downBreakPoint = useMobile();

  async function onExit() {
    await client.clearStore();
    client.writeData({ data: { isLoggedIn: false } });

    localStorage.clear();
  }

  async function onChangeResetPassword() {
    const senhaAtual = getValues().senhaAtual;
    const senhaNova = getValues().senhaNova;

    if (!senhaAtual) {
      enqueueSnackbar('Senha atual não pode está vazia!', { variant: 'error' });

      return;
    }

    if (!senhaNova) {
      enqueueSnackbar('Senha nova não pode está vazia!', { variant: 'error' });

      return;
    }

    if (senhaAtual === senhaNova) {
      enqueueSnackbar('As senhas não podem estar iguais!', { variant: 'error' });

      return;
    }

    if (senhaNova.length < 8) {
      enqueueSnackbar('Senha deve conter no mínimo 8 caracteres!', { variant: 'error' });

      return;
    }

    try {
      await alterarSenha({
        variables: { senhaAtual: senhaAtual, senhaNova: senhaNova },
      });

      enqueueSnackbar('Senha alterada com sucesso!', { variant: 'success' });
      handleCloseDrawer();
      reset();

      setTimeout(async () => {
        await onExit();
      }, 1000);
    } catch (error) {
      // enqueueSnackbar('Não foi possível alterar a senha!', { variant: 'error' });
      enqueueSnackbar('Certifique que informou a senha corretamente!', { variant: 'error' });
    }
  }

  useEffect(() => {
    function handleClickOutside(event: any) {
      if (childRef.current && !childRef.current.contains(event.target)) {
        handleCloseDrawer();
        setValue('senhaAtual', '');
        setValue('senhaNova', '');
        setSecurity(true);
        setConfirmSecurity(true);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Grid
      style={{
        display: open ? 'flex' : 'none',
        justifyContent: 'end',
        position: 'fixed',
        width: '100vw',
        zIndex: 10,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        height: '100vh',
      }}
    >
      <StyledChild
        container
        item
        xs={12}
        sm={8}
        md={6}
        lg={4}
        ref={childRef}
        style={{
          position: 'relative',
          backgroundColor: '#fff',
          height: 'auto',
          opacity: 1,
          zIndex: 11,
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 25,
        }}
      >
        <img src={ImagePassword} alt="Alterar Senha" style={{ width: 140, height: 115 }} />
        <TypographyCN
          text="Alterar Senha"
          fontType="Proxima Nova"
          weight="big"
          size={downBreakPoint ? 'desk-simple' : 'big-title'}
        />
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            width: downBreakPoint ? '100%' : '80%',
            gap: 20,
            paddingBottom: 20,
          }}
        >
          <Controller
            as={
              <InputCN
                placeholder="Senha Atual"
                type={security ? 'password' : 'text'}
                autoComplete="off"
                maxLength={58}
                fullWidth
                endAdornment={
                  <Box
                    style={{
                      cursor: 'pointer',
                      position: 'absolute',
                      // paddingRight: 10,
                      right: 10,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    onClick={() => setSecurity(!security)}
                  >
                    <img
                      src={security ? EyesClosedIcon : EyesOpenIcon}
                      alt="Password"
                      style={{
                        width: 25,
                        height: 25,
                      }}
                    />
                  </Box>
                }
              />
            }
            {...{ control, name: 'senhaAtual' }}
            style={{
              paddingRight: 30,
            }}
          />

          <Controller
            as={
              <InputCN
                placeholder="Nova Senha"
                type={confirmSecurity ? 'password' : 'text'}
                endAdornment={
                  <Box
                    style={{
                      cursor: 'pointer',
                      position: 'absolute',
                      // paddingRight: 10,
                      right: 10,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    onClick={() => setConfirmSecurity(!confirmSecurity)}
                  >
                    <img
                      src={confirmSecurity ? EyesClosedIcon : EyesOpenIcon}
                      alt="Nova Password"
                      style={{
                        width: 25,
                        height: 25,
                      }}
                    />
                  </Box>
                }
              />
            }
            {...{ control, name: 'senhaNova' }}
            style={{
              paddingRight: 30,
            }}
          />
        </Box>
        <Box
          style={{
            display: 'flex',
            width: downBreakPoint ? '100%' : undefined,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: downBreakPoint ? 15 : 25,
          }}
        >
          <CustomButton color="secondary" text="ALTERAR" onClick={onChangeResetPassword} />
          <CustomButton
            variant="outlined"
            color="error"
            text="CANCELAR"
            onClick={() => {
              handleCloseDrawer();
              setValue('senhaAtual', '');
              setValue('senhaNova', '');
              setSecurity(true);
              setConfirmSecurity(true);
            }}
          />
        </Box>
      </StyledChild>
    </Grid>
  );
}
